<template>
  <div class="sign-up">
    <div class="sign-up__images-area">
      <MCarousel auto-play>
        <Slide
          v-for="(image, i) in images"
          :key="`image-${i}`"
          class="sign-up__image-item"
        >
          <picture>
            <source media="(max-width: 768px)" :srcset="image.sp" />
            <img :src="image.pc" :alt="image.alt" />
          </picture>
        </Slide>
      </MCarousel>
    </div>
    <div class="sign-up__inner">
      <AHeading class="sign-up__header" :level="1">新規会員登録</AHeading>
      <form class="sign-up__container" @submit.prevent="signup()">
        <div class="sign-up__parts">
          <ALabel required for="email">メールアドレス</ALabel>
          <span v-if="errorsEmail" class="sign-up__parts-error-body"
            >不正なアドレスです</span
          >
          <AInput
            class="sign-up__input"
            type="text"
            name="email"
            :value="email"
            placeholder="onecareer@onecareer.jp"
            :validate-rule="emailValidateRule"
            validate-mode="blur"
            @input="handleInput($event, 'email')"
            @validateBlur="handleValidateBlur($event, 'email')"
          />
        </div>
        <div class="sign-up__parts">
          <ALabel required for="email">パスワード</ALabel>
          <span v-if="errorsPassword" class="sign-up__parts-error-body"
            >不正なパスワードです</span
          >
          <ABody class="sign-up__label-sub" size="small"
            >8文字以上の半角英数字記号</ABody
          >
          <AInput
            class="sign-up__input"
            type="password"
            name="password"
            :value="password"
            placeholder="************"
            :validate-rule="passwordValidateRule"
            validate-mode="blur"
            visible-password
            @input="handleInput($event, 'password')"
            @validateBlur="handleValidateBlur($event, 'password')"
          />
        </div>
        <div class="sign-up__button-wrapper">
          <AButton
            round
            fill
            bold
            size="medium"
            :disabled="buttonDisabled"
            :processing="processing"
            :prevent-default="buttonDisabled"
            class="sign-up__button"
            >無料で登録する</AButton
          >
        </div>
      </form>
      <div class="sign-up__link">
        <ALink to="/users/sign_in">ログインはこちら ></ALink>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Slide } from 'vue3-carousel';
import * as api from '@/api/index';
import AButton from '@/components/atoms/AButton/index.vue';
import AHeading from '@/components/atoms/ATypography/AHeading/index.vue';
import ABody from '@/components/atoms/ATypography/ABody/index.vue';
import ALabel from '@/components/atoms/ATypography/ALabel/index.vue';
import ALink from '@/components/atoms/ATypography/ALink/index.vue';
import AInput from '@/components/atoms/AForm/AInput/index.vue';
import MCarousel from '@/components/molecules/MCarousel/index.vue';
import {
  emailValidateRule,
  passwordValidateRule
} from '@/helpers/validateRules';
import {
  ErrorSystemMessage,
  ErrorSendConfirmMailMessage
} from '@/helpers/errorMessages';

export default defineComponent({
  name: 'SignUp',
  components: {
    AButton,
    AHeading,
    ABody,
    ALabel,
    ALink,
    AInput,
    MCarousel,
    Slide
  },
  data() {
    return {
      email: '',
      password: '',
      processing: false,
      errorsEmail: false,
      errorsPassword: false,
      emailValidateRule: emailValidateRule,
      passwordValidateRule: passwordValidateRule,
      images: [
        {
          pc: require('@/assets/images/ocp_appeal1_pc.jpg'),
          sp: require('@/assets/images/ocp_appeal1_sp.jpg'),
          alt: '転職傾向を見てみよう'
        },
        {
          pc: require('@/assets/images/ocp_appeal2_pc.jpg'),
          sp: require('@/assets/images/ocp_appeal2_sp.jpg'),
          alt: '無料面談で相談しよう'
        },
        {
          pc: require('@/assets/images/ocp_appeal3_pc.jpg'),
          sp: require('@/assets/images/ocp_appeal3_sp.jpg'),
          alt: '選考内容を見てみよう'
        }
      ]
    };
  },
  computed: {
    buttonDisabled() {
      return (
        !this.email || !this.password || this.errorsEmail || this.errorsPassword
      );
    }
  },
  methods: {
    handleInput(value, target) {
      if (target === 'email') {
        this.email = value;
      } else if (target === 'password') {
        this.password = value;
      }
    },
    handleValidateBlur(error, target) {
      if (target === 'email') {
        this.errorsEmail = error;
      } else if (target === 'password') {
        this.errorsPassword = error;
      }
    },
    async signup() {
      // ボタン非活性の場合は無効
      if (this.buttonDisabled) return false;

      try {
        this.processing = true;
        await api
          .post('/api/v1/signup', {
            email: this.email,
            password: this.password,
            utm_source: this.$route.query.utm_source,
            utm_medium: this.$route.query.utm_medium,
            utm_campaign: this.$route.query.utm_campaign,
            utm_term: this.$route.query.utm_term,
            utm_content: this.$route.query.utm_content
          })
          .finally(() => {
            this.processing = false;
          });
        this.$router.push('/users/wait');
      } catch (error) {
        if (error?.response?.status === 409) {
          alert(ErrorSendConfirmMailMessage);
          this.$router.push('/users/sign_in');
        } else {
          alert(ErrorSystemMessage);
        }
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.sign-up {
  @include formBase;

  &__images-area {
    margin: 48px auto 68px;
    width: 100%;
    height: auto;
    border-radius: 4px;
    @include mq {
      margin: 24px 0 51px;
    }
  }

  &__image-item {
    position: relative;
    height: auto;

    img {
      width: 100%;
      height: auto;
      padding: 0 36px;

      @include mq {
        padding: 0 16px;
      }
    }
  }

  &__inner {
    padding: 0 32px 104px;
    max-width: 407px;
    margin: auto;

    @include mq() {
      max-width: 100%;
      padding: 0 16px 32px;
    }
  }

  &__parts-error-body {
    margin-left: 12px;
    font-size: 10px;
    color: $text-attention-color;
    line-height: $line-height-basic;
  }

  &__checkbox-body {
    margin-left: 8px;
  }

  &__link {
    margin-top: 12px;
    text-align: right;
  }

  &__header {
    margin-bottom: 32px;
  }

  &__label-sub {
    margin-top: 4px;
  }
}
</style>
